import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  deleteChannelConfig,
  getApiErrorMessage,
  listChannelConfigs,
  listDepartments,
} from '../../api/client';
import type { ChannelConfig, DepartmentSummary } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function ChannelsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [configs, setConfigs] = useState<ChannelConfig[]>([]);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const departmentFilter = searchParams.get('departmentId') ?? '';
  const canCreate = user && canWritePlatformConfig(user);
  const canDelete = user && canEditPlatformConfig(user);

  useEffect(() => {
    listDepartments().then(setDepartments).catch(() => undefined);
  }, []);

  useEffect(() => {
    setLoading(true);
    listChannelConfigs(departmentFilter || undefined)
      .then(setConfigs)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [departmentFilter]);

  async function handleDelete(id: string) {
    if (!window.confirm('Deactivate this channel config?')) return;
    setDeletingId(id);
    setError('');
    try {
      await deleteChannelConfig(id);
      setConfigs((prev) =>
        prev.map((c) => (c.id === id ? { ...c, isActive: false } : c)),
      );
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <div className="page-header" style={{ marginTop: 0 }}>
        <div>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Map inbound channels to departments. Credentials are stored encrypted server-side.
          </p>
        </div>
        {canCreate && (
          <Link to={PLATFORM_CONFIG.channelsNew} className="btn btn-primary">
            Add mapping
          </Link>
        )}
      </div>

      {!canDelete && (
        <div className="alert alert-info">
          You have read-only access to channel configuration.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      <div className="filters card card-body" style={{ marginBottom: '1rem' }}>
        <div className="form-group">
          <label htmlFor="channel-dept-filter">Department</label>
          <select
            id="channel-dept-filter"
            className="form-control"
            value={departmentFilter}
            onChange={(e) => {
              const next = e.target.value;
              if (next) setSearchParams({ departmentId: next });
              else setSearchParams({});
            }}
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">Loading channel configs…</div>
        ) : configs.length === 0 ? (
          <div className="empty-state">No channel mappings configured.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Identifier</th>
                  <th>Label</th>
                  <th>Credentials</th>
                  <th>Department</th>
                  <th>Status</th>
                  {canDelete && <th />}
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Badge label={c.channelType.name} className="badge-info" />
                    </td>
                    <td>
                      {canDelete ? (
                        <Link to={PLATFORM_CONFIG.channel(c.id)}>{c.identifier}</Link>
                      ) : (
                        c.identifier
                      )}
                    </td>
                    <td>{c.label}</td>
                    <td>{c.hasCredentials ? 'Stored' : '—'}</td>
                    <td>{c.department.name}</td>
                    <td>
                      <Badge
                        label={c.isActive ? 'ACTIVE' : 'INACTIVE'}
                        className={c.isActive ? 'badge-success' : 'badge-neutral'}
                      />
                    </td>
                    {canDelete && (
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn btn-danger-ghost"
                          disabled={deletingId === c.id}
                          onClick={() => handleDelete(c.id)}
                        >
                          {deletingId === c.id ? '…' : 'Deactivate'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
